import React, { forwardRef, useImperativeHandle } from 'react';
import { Props as ExtraFieldsAreaProps } from './ExtraFieldsArea';
import { ExtraFieldsArea } from './ExtraFieldsArea';
import { FormContext } from 'react-hook-form';
import { useExtraFieldsFormSimple } from './useExtraFieldsFormSimple';
import { ExtraFieldInternalValue } from '../extraFieldInternalValue';
import { useObjectExtraFields } from '../useObjectExtraFields';
import { useExtraFieldsFormValidation } from '../useExtraFieldsFormValidation';
import * as yup from 'yup';
import { Loading } from './ExtraFieldsAreaContent';

type Props = ExtraFieldsAreaProps & {
  onChange: (
    extraFields: ExtraFieldInternalValue[],
    recordTypeName: string | undefined,
  ) => void;
  values?: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null; // null means no recordType
};

export type ExtraFieldsAreaSimpleRef = {
  triggerValidation: () => Promise<boolean>;
};

// ExtraFieldsArea use inside 'react-hook-form'. That class hides these implementation details. And allows to use the component as normal react.
// That implementation is puzzled, better to rewrite ExtraFieldsArea not to use 'react-hook-form'.
export const ExtraFieldsAreaSimple = forwardRef<
  ExtraFieldsAreaSimpleRef,
  Props
>(({ onChange, values = [], recordTypeName, ...restProps }, ref) => {
  const { rawObjectExtraFields, getFormFields, loading } = useObjectExtraFields(
    restProps.objectApiName,
  );

  const formFields = getFormFields(restProps.formId);

  const { applyExtraFieldsValidationSchema } = useExtraFieldsFormValidation({
    extraFieldFormInfos: formFields,
    contextParams: restProps.contextParams ?? [],
  });

  const validationSchema = applyExtraFieldsValidationSchema(yup.object<any>());

  const { formMethods } = useExtraFieldsFormSimple({
    values,
    recordTypeName,
    availableRecordTypes: rawObjectExtraFields?.availableRecordTypes ?? [],
    onChange,
    validationSchema,
  });

  useImperativeHandle(ref, () => ({
    triggerValidation: () =>
      loading || !rawObjectExtraFields
        ? Promise.resolve(true)
        : formMethods.triggerValidation(),
  }));

  if (loading) return <Loading />;
  if (!rawObjectExtraFields) return null;

  return (
    <FormContext {...formMethods}>
      <ExtraFieldsArea {...restProps} />
    </FormContext>
  );
});

ExtraFieldsAreaSimple.displayName = 'ExtraFieldsAreaSimple';
